import { useCallback, useEffect, useState } from 'react'
import { fetchPublic } from '../api/client'
import { ecrireContenuEnCache, lireContenuEnCache } from './contenuEnCache'

// Sert d'abord la copie embarquée à la compilation, puis la remplace par les
// données de l'API dès qu'elles arrivent. Si l'API ne répond pas, le visiteur
// voit un contenu éventuellement daté plutôt qu'une page vide.
//
// `status` ('pending' | 'success' | 'error') distingue une requête encore en
// cours d'une requête qui a authentiquement échoué — une distinction dont
// useBookingSettings a besoin (elle ne peut pas se contenter de comparer la
// donnée à sa copie de secours, ce qui ne dit pas si la requête est encore en
// vol ou si elle a définitivement échoué). Les pages légales le consultent
// aussi, avec `reessayer`, pour leur état « contenu indisponible ». Le pied de
// page, lui, n'en a pas besoin : useSiteContent ci-dessous garde exactement la
// même signature et le même comportement qu'avant pour ses appelants
// existants, qui ne voient jamais ce second export.
export function useSiteContentState(path, fallback) {
  // Le cache d'abord, la copie embarquée ensuite : la valeur mémorisée vient
  // d'une réponse réelle de l'API, donc postérieure à la compilation qui a
  // produit `fallback`. Fonction d'initialisation et non valeur directe, pour
  // que le stockage ne soit lu qu'au premier rendu.
  const [data, setData] = useState(() => lireContenuEnCache(path) ?? fallback)
  const [status, setStatus] = useState('pending')
  // Incrémenté par `reessayer` : c'est la seule dépendance de l'effet qui
  // change sans que le chemin change, donc le seul moyen de relancer la
  // requête sans remonter le composant. Les pages légales s'en servent pour
  // le bouton « Réessayer » de leur état d'indisponibilité.
  const [tentative, setTentative] = useState(0)
  const reessayer = useCallback(() => setTentative((n) => n + 1), [])

  useEffect(() => {
    let cancelled = false
    setStatus('pending')
    fetchPublic(path)
      .then((fresh) => {
        // Écrit AVANT le test d'annulation : la réponse est bonne, et la
        // mémoriser profite au prochain montage même si celui-ci est déjà
        // démonté.
        ecrireContenuEnCache(path, fresh)
        if (cancelled) return
        setData(fresh)
        setStatus('success')
      })
      .catch((error) => {
        console.error(`Contenu « ${path} » indisponible, copie de secours utilisée`, error)
        if (cancelled) return
        // `status` ne tient JAMAIS compte du cache : useBookingSettings s'en
        // sert pour décider d'injecter ou non le module Planity, et une clé
        // mémorisée peut avoir été révoquée depuis. Voir le commentaire de
        // ce hook-là.
        setStatus('error')
      })
    return () => {
      cancelled = true
    }
  }, [path, tentative])

  return { data, status, reessayer }
}

export function useSiteContent(path, fallback) {
  return useSiteContentState(path, fallback).data
}
